import type { RxJsonSchema } from 'rxdb'

/**
 * Mention Document Type (Denormalized for RxDB)
 */
export interface MentionDocument {
  id: string
  envId: string
  entityType: string
  entityId: string
  userId: string
  /** Origin of the mention (e.g. comment, document). Backend-provided, may be null. */
  sourceType: string | null
  /** ID of the originating entity (e.g. comment id). Backend-provided, may be null. */
  sourceId: string | null
  isRead: boolean
  readAt: number | null
  isDeleted: boolean

  // Denormalized user info
  userName: string | null
  userEmail: string | null

  // Audit fields
  createdOn: number
  createdBy: string
  updatedOn: number
  updatedBy: string
  deletedOn: number | null
  deletedBy: string | null

  // RxDB replication metadata
  updatedAt?: number
}

/**
 * RxDB Schema for Mention
 */
export const mentionSchema: RxJsonSchema<MentionDocument> = {
  version: 1,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: {
      type: 'string',
      maxLength: 36,
    },
    envId: {
      type: 'string',
      maxLength: 36,
    },
    entityType: {
      type: 'string',
      maxLength: 50,
    },
    entityId: {
      type: 'string',
      maxLength: 36,
    },
    userId: {
      type: 'string',
      maxLength: 36,
    },
    sourceType: {
      type: ['string', 'null'],
      maxLength: 50,
    },
    sourceId: {
      type: ['string', 'null'],
      maxLength: 36,
    },
    isRead: {
      type: 'boolean',
      default: false,
    },
    readAt: {
      type: ['number', 'null'],
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    isDeleted: {
      type: 'boolean',
      default: false,
    },

    // Denormalized fields
    userName: {
      type: ['string', 'null'],
      maxLength: 100,
    },
    userEmail: {
      type: ['string', 'null'],
      maxLength: 255,
    },

    // Audit fields
    createdOn: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    createdBy: {
      type: 'string',
      maxLength: 36,
    },
    updatedOn: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    updatedBy: {
      type: 'string',
      maxLength: 36,
    },
    deletedOn: {
      type: ['number', 'null'],
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    deletedBy: {
      type: ['string', 'null'],
      maxLength: 36,
    },

    // RxDB replication metadata
    updatedAt: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
  },
  required: [
    'id',
    'envId',
    'entityType',
    'entityId',
    'userId',
    'isRead',
    'isDeleted',
    'createdOn',
    'createdBy',
    'updatedOn',
    'updatedBy',
  ],
  indexes: [
    'envId',
    'userId',
    'entityId',
    'updatedOn',
    ['userId', 'isRead'],
    ['entityId', 'isDeleted'],
  ],
}

export const mentionMigrationStrategies = {
  // v0 -> v1: backfill `sourceId`, `sourceType` (added, nullable).
  1: (oldDoc: any): any => ({
    ...oldDoc,
    sourceId: oldDoc.sourceId ?? null,
    sourceType: oldDoc.sourceType ?? null,
  }),
}

